import { Factory, trait } from 'miragejs';
import { faker } from '@faker-js/faker';
import { add } from 'date-fns';
import { BlocklistFilterSource } from 'garaje/utils/enums';

function getStatus(report = []) {
  if (report.every((r) => r.status === 'approved')) {
    return 'approved';
  }
  if (report.any((r) => r.status === 'denied')) {
    return 'denied';
  }
  return 'review';
}

function setFlowOnInvite(invite, flow) {
  const userData = invite.userData ?? [];
  let purposeOfVisit = userData.find((datum) => datum.field === 'Purpose of visit');
  if (purposeOfVisit) {
    purposeOfVisit.value = flow.name;
  } else {
    purposeOfVisit = { field: 'Purpose of visit', value: flow.name };
    userData.push(purposeOfVisit);
  }
  invite.update({
    flow,
    flowName: flow.name,
    userData,
  });
}

export default Factory.extend({
  fullName: () => faker.person.fullName(),
  email: () => faker.internet.email(),
  expectedArrivalTime: () => new Date(),
  arrived: false,
  beenHereBefore: false,
  userData: () => [],
  locationNames: () => [],
  additionalGuests: null,
  nda: null,
  guestUpdatedAt: null,
  originalNdaSignDate: null,
  ndaAvailable: null,
  inviterName: null,
  skipGuestNotification: false,
  preregistrationComplete: false,

  groupName: null,
  hostName: null,
  visitorCategory: 'visitor',

  photoUrl: null,

  /*
   * approval status for block list and other approval checks
   *
   * @return {Object} invite results against a block list
   *   status [review|pending|approved|denied]
   */
  approvalStatus() {
    return { report: undefined, status: undefined, reviewerName: undefined };
  },

  afterCreate(invite, server) {
    const flow = invite.flow ?? server.schema.flows.all().models[0];
    setFlowOnInvite(invite, flow);
  },

  delivery: trait({
    afterCreate(invite, server) {
      const flow = server.schema.flows.findOrCreateBy({ name: 'Delivery' });
      setFlowOnInvite(invite, flow);
    },
  }),

  interview: trait({
    afterCreate(invite, server) {
      const flow = invite.flow ?? server.schema.flows.findOrCreateBy({ name: 'Interview' });
      setFlowOnInvite(invite, flow);
    },
  }),

  employeeRegistration: trait({
    employeeScreeningFlow: true,
    afterCreate(invite, server) {
      if (!server.schema.employeeScreeningFlows.first()) {
        server.create('employee-screening-flow');
      }
      const flow = server.schema.employeeScreeningFlows.first();
      setFlowOnInvite(invite, flow);
    },
  }),

  fromTenant: trait({
    afterCreate(invite, server) {
      const tenant = invite.tenant || server.create('tenant', 'withSuites');

      invite.update({
        locationId: tenant.location?.id,
        tenantId: tenant.id,
        suiteIds: tenant.suites.models.map((s) => s.id),
        groupName: faker.person.fullName(),
        hostName: faker.person.fullName(),
      });
    },
  }),

  asPropertyWalkup: trait({
    checkedInAt: new Date(),
    flowName: 'Property walk-up',
    visitorCategory: 'property',
    afterCreate(invite, server) {
      const flow = server.schema.flows.findOrCreateBy({ name: 'Property walk-up' });
      setFlowOnInvite(invite, flow);
    },
  }),

  withPrivateNotes: trait({
    privateNotes: () => faker.word.words({ count: { min: 16, max: 64 } }),
  }),

  withSharedNotes: trait({
    propertyNotes: () => faker.word.words(faker.helpers.arrayElement([2, 4, 6])),
  }),

  asSignedIn: trait({
    afterCreate(invite, server) {
      const signedInAt = add(invite.expectedArrivalTime, { minutes: faker.helpers.arrayElement([0, 5, 10]) });

      invite.update({ arrived: true });

      server.create('entry', {
        signedInAt,
        invite,
        fullName: invite.fullName,
        email: invite.email,
      });
    },
  }),

  asCheckedIn: trait({
    afterCreate(invite, server) {
      const checkedInAt = add(invite.expectedArrivalTime, { minutes: faker.helpers.arrayElement([0, 5, 10]) });
      invite.update({
        checkedInAt,
        multiTenancyVisitorNotificationLogs: [server.create('notification-log')],
      });

      const configuration = server.schema.connectLocationConfigurations.findBy({
        locationId: invite.location?.id,
      });

      if (configuration?.automaticEntryCreationOnSignIn) {
        server.create('entry', {
          checkedInAt,
          invite,
          fullName: invite.fullName,
          email: invite.email,
          propertyNotes: invite.propertyNotes,
        });
      }
    },
  }),

  /** Various blocklist filter states **/
  asLocationBlocklistFailed: trait({
    afterCreate(invite, server) {
      const blocklistFilter = server.create('blacklist-filter', {
        createdBy: 'test admin',
        fullName: invite.fullName,
        reason: 'BOLO',
      });

      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'blacklist',
          result: 'fail',
          blacklistFilterId: blocklistFilter.id,
          blacklistFilterSource: BlocklistFilterSource.LOCATION,
          blacklistFilterSourceId: null, // todo - id of blocklistFilter.property
          status: 'review',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asPropertyBlocklistFailed: trait({
    afterCreate(invite, server) {
      const blocklistFilter = server.create('blacklist-filter', {
        createdBy: 'test admin',
        fullName: invite.fullName,
        reason: 'BOLO',
      });

      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'blacklist',
          result: 'fail',
          blacklistFilterId: blocklistFilter.id,
          blacklistFilterSource: BlocklistFilterSource.PROPERTY,
          blacklistFilterSourceId: null, // todo - id of blocklistFilter.property
          status: 'review',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asPropertyBlocklistFailedApproved: trait({
    afterCreate(invite, server) {
      const blocklistFilter = server.create('blacklist-filter', {
        createdBy: 'test admin',
        fullName: invite.fullName,
        reason: 'BOLO',
      });

      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'blacklist',
          result: 'fail',
          blacklistFilterId: blocklistFilter.id,
          blacklistFilterSource: BlocklistFilterSource.PROPERTY,
          blacklistFilterSourceId: null, // todo - id of blocklistFilter.property
          status: 'approved',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asPropertyBlocklistFailedDenied: trait({
    afterCreate(invite, server) {
      const blocklistFilter = server.create('blacklist-filter', {
        createdBy: 'test admin',
        fullName: invite.fullName,
        reason: 'BOLO',
      });

      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'blacklist',
          result: 'fail',
          blacklistFilterId: blocklistFilter.id,
          blacklistFilterSource: BlocklistFilterSource.PROPERTY,
          blacklistFilterSourceId: null, // todo - id of blocklistFilter.property
          status: 'denied',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asLocationBlocklistFailedApproved: trait({
    afterCreate(invite, server) {
      const blocklistFilter = server.create('blacklist-filter', {
        createdBy: 'test admin',
        fullName: invite.fullName,
        reason: 'BOLO',
      });

      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'blacklist',
          result: 'fail',
          blacklistFilterId: blocklistFilter.id,
          blacklistFilterSource: BlocklistFilterSource.LOCATION,
          blacklistFilterSourceId: null, // todo - id of blocklistFilter.property
          status: 'approved',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asLocationBlocklistFailedDenied: trait({
    afterCreate(invite, server) {
      const blocklistFilter = server.create('blacklist-filter', {
        createdBy: 'test admin',
        fullName: invite.fullName,
        reason: 'BOLO',
      });

      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'blacklist',
          result: 'fail',
          blacklistFilterId: blocklistFilter.id,
          blacklistFilterSource: BlocklistFilterSource.LOCATION,
          blacklistFilterSourceId: null, // todo - id of blocklistFilter.property
          status: 'denied',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  /** Invites for an "approval required" flow */
  asOutgoingInviteApprovalRequired: trait({
    afterCreate(invite) {
      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          result: 'fail',
          reviewable: true,
          source: 'requires-approval',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asOutgoingInviteApprovalRequiredApproved: trait({
    afterCreate(invite) {
      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'requires-approval',
          status: 'approved',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asOutgoingInviteApprovalRequiredDenied: trait({
    afterCreate(invite) {
      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'requires-approval',
          status: 'denied',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asOutgoingPropertyInviteApprovalRequiredApproved: trait({
    afterCreate(invite) {
      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'property-invite-approval',
          status: 'approved',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  asOutgoingPropertyInviteApprovalRequiredDenied: trait({
    afterCreate(invite) {
      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'property-invite-approval',
          status: 'denied',
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  /** ID verification check states */
  asIdVerificationFailed: trait({
    afterCreate(invite /*, server*/) {
      const report = [
        ...(invite.approvalStatus?.report || []),
        {
          source: 'identity-verification',
          result: 'fail',
          reason: 'ID verification failed',
          reviewable: true,
        },
      ];

      invite.update({
        approvalStatus: {
          status: getStatus(report),
          report,
        },
      });
    },
  }),

  withPhoto: trait({
    photoUrl: 'https://picsum.photos/200/200',
  }),
});
